import { scaleOpacity } from '@dynamic/ui';
import { createGlobalStyle, css } from 'styled-components';

import { ThemeConfig } from '#/service/config/config.types';
import { GradientDirection } from '#/service/constants';
import { GradientColor, isGradientColorKey } from '#/service/theme/colors';
import {
  falsyToNull,
  getFontStyle,
  getLinearGradient,
  pascal,
  remify,
} from '#/service/utils';

import { TOP_MENU_HEIGHT_PX } from './Header/Menu.utils';

interface CreateGlobalStyleParams {
  theme: ThemeConfig;
}

/**
 * Focus styles applicable for all content cards (like UpNext, Swiper, Grid, etc).
 * It uses `:after` pseudo-element to style all browsers equally (I am looking at you, Safari)
 * Once Safari fixes itself a much simpler styling approach can be used
 */
export const itemFocusStyle = css`
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    box-shadow: inset 0 0 0 3px var(--itemFocusFrame);
  }
`;

/** Helper function to be able to build the linear gradients for buttons */
export const buildButtonGradients = (
  key: string,
  value: string,
  gradientColor?: GradientColor
) => {
  if (!isGradientColorKey(key)) {
    return '';
  }

  const cssVariable = gradientColor?.[key];

  if (cssVariable && cssVariable.colors.length > 1) {
    const { colors, direction } = cssVariable;
    const gradientDirection =
      GradientDirection[direction ?? 'RIGHT'] ?? GradientDirection.RIGHT;

    return `
      ${getLinearGradient(key, colors, gradientDirection)}
      ${getLinearGradient(key, colors, gradientDirection, 'Hover')}
      ${getLinearGradient(key, colors, gradientDirection, 'Disabled')}
    `;
  }

  const colorList = [{ color: value }, { color: value }];

  // If there is no gradientColor defined, we use the solid linear gradient color as fallback.
  return `
    ${getLinearGradient(key, colorList, GradientDirection.RIGHT)}
    ${getLinearGradient(key, colorList, GradientDirection.RIGHT, 'Hover')}
    ${getLinearGradient(key, colorList, GradientDirection.RIGHT, 'Disabled')}
  `;
};

/** Place where all app-level css variables are defined. Exported for tests purpose only */
export const appCssVariables = css`
  :root {
    font-size: 18px;

    /* CSS Variables */

    /* Maps our theme.color into a set of CSS Variables */
    ${({ theme }) => {
      return Object.entries(theme.color)
        .filter(Boolean)
        .map(([k, v]) => `--${k}: ${v};`);
    }}

    /* Maps our theme.fonts.textClasses into a set of CSS Variables */
    /* Example transform: theme.fonts.textClasses.CAPTION_2 -> --fontStyleCaption2 */
    ${({ theme }) => {
      return Object.entries(theme.fonts.textClasses)
        .filter(Boolean)
        .map(([k, v]) => `--fontStyle${pascal(k)}: ${getFontStyle(v)};`);
    }}

    /* Custom colors that are not in the theme object directly, but still are useful to be defined globally */
    --pageParagraphForegroundSubtle: ${({ theme }) =>
      scaleOpacity(theme.color.pageParagraphForeground, 0.7)};

    /* shadow and overlay backdrop colors are fixed colors by design: 60%/80% black */
    --shadowColor: #0009;
    --overlayBackdrop: #0009;
    --overlayBackdropDarker: #000c;

    /* Derived colors for creating gradients out of "imageOverlayBackground" */
    --imageOverlayBackgroundGradientFrom: ${({ theme }) =>
      scaleOpacity(
        falsyToNull(theme.color.imageOverlayBackground) ?? '#000',
        0.8
      )};
    --imageOverlayBackgroundGradientTo: ${({ theme }) =>
      scaleOpacity(
        falsyToNull(theme.color.imageOverlayBackground) ?? '#000',
        0.2
      )};

    /* Hover input colors */
    --inputValidationHover: ${({ theme }) =>
      scaleOpacity(theme.color.inputValidation, 0.7)};
    --selectionControlsBackgroundHover: ${({ theme }) =>
      scaleOpacity(theme.color.selectionControlsBackground, 0.7)};

    /* Input Sizes */
    --inputBorderRadius: ${remify(8)};

    /* Scrim background color for playback */
    --playbackScrimColor: #0009;
    --playbackControlsOverlay: ${({ theme }) =>
      scaleOpacity(theme.color.videoPlayerOverlayBackground, 0.9)};

    /* Derived colors for ad elements */
    --playbackAdBackground: ${({ theme }) =>
      scaleOpacity(theme.color.videoPlayerOverlayBackground, 0.4)};

    /* Derived colors for seekbar */
    --playbackRangeBase: ${({ theme }) =>
      scaleOpacity(theme.color.videoPlayerOverlayForeground, 0.2)};
    --playbackRangeBuffered: ${({ theme }) =>
      scaleOpacity(theme.color.videoPlayerOverlayForeground, 0.54)};
    --playbackRangeFill: ${({ theme }) =>
      theme.color.videoPlayerProgressBarForeground};

    /* Derived linear gradient colors for button background & borders */
    ${({ theme }) => {
      const { color, gradientColor } = theme;
      const regex = /^button.*(Background|Border)$/;

      return Object.entries(color)
        .filter(([key]) => regex.test(key))
        .map(([key, value]) => buildButtonGradients(key, value, gradientColor));
    }}
  }
`;

// This is for global styles only. Do not put component-specific rules here.
export const GlobalStyle = createGlobalStyle<CreateGlobalStyleParams>`
  ${appCssVariables}

  html,
  body,
  #root {
    margin: 0;
    padding: 0;
    overscroll-behavior: none;
    font-synthesis: none;
  }

  body {
    /* make this available for components outside root, like dialogs */
    --menuHeight: calc(${remify(TOP_MENU_HEIGHT_PX)} + var(--bannerHeight, 0px));
    
    color: var(--pageParagraphForeground);
    background-color: var(--pageBackground);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Hide an element, but keep it accessible for screen readers */
  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    border: 0;
    padding: 0;
    white-space: nowrap;
    clip-path: inset(100%);
    overflow: hidden;
  }

  code,
  pre {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
      monospace;
    margin: 0;
    padding: 1rem;
  }

  .icon *:not(.keep-color) {
    fill: currentcolor;
  }

  a {
    color: var(--pageLinkForeground);
    text-decoration: none;
    outline: none;
    border-radius: 1px;
  }

  a:focus-visible {
    outline: 3px solid var(--itemFocusFrame);
    outline-offset: 3px;
  }
  
  a:hover,
  a:focus:hover {
    opacity: 0.7;
  }

  /*
   * Focus for keyboard navigation in Swiper and Grid items. Some things to note:
   * Simple INLINE outline is used to work with overflow: hidden (used in Swiper by default)
   * You can't just change it to use overflow-x: clip as Safari does not support it atm.
   * So to make it useful (but not super pretty) inline outline is used.
   * The same goes to Grid items. It's easiest to target them through such selector
   * Pseudo-element :after was used to mitigate outline problem with, you guessed it, Safari
   * The static color used is the same color as Safari and Firefox use for outlines by default
   */
  .swiper, div[class^='GridItem\-'] {
    div:focus-visible {
     ${itemFocusStyle}
    }
  }
`;
